import { Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import Styles from "./ListingDetails.module.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BoltIcon from "@mui/icons-material/Bolt";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatDateType } from "../../../../commons/FormatDate";
import { useDispatch, useSelector } from "react-redux";
import { fetchListingTermsConfiguration } from "../../../../Redux/Slices/gnaAdminSlice";
import ViewFinalizeTerms from "../../ConGen/ViewFinalizeTerms/ViewFinalizeTerms";
import Charges from "../../ConGen/Charges/Charges";
import ViewConsent from "../../ConGen/ViewConsent/ViewConsent";
import ViewDoc from "../../ConGen/ViewDoc/ViewDoc";
import ScheduleModal from "../../Schedule/ScheduleModal";
import { fetchSchedule } from "../../../../Redux/Slices/scheduleSlice";
import { fetchMatchmakingResults } from "../../../../Redux/Slices/bankingSlice";
import DiscomDetails from "./DiscomDetails";
import { openBilateralChat } from "../../Banking/CreateEdit/helpers";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const formatISODate = (isoStr) => {
  if (!isoStr) return { date: "-", time: "" };
  const [datePart, timePart] = isoStr.split("T");
  if (!datePart) return { date: isoStr, time: "" };
  const [yr, mo, dy] = datePart.split("-");
  return {
    date: `${parseInt(dy)} ${MONTHS[parseInt(mo) - 1]} ${yr}`,
    time: timePart ? timePart.slice(0, 5) : "",
  };
};

const ListingDetails = ({ open, handleClose, data, type }) => {
  const bilateral = useSelector(
    (state) => state?.gnaAdminSlice?.fetchListingTermsConfiguration?.data?.page
  );
  const bankingList = useSelector(
    (state) => state?.bankingSlice?.matchmakingData
  );

  const dispatch = useDispatch();
  useEffect(() => {
    if (type === "banking") {
      dispatch(
        fetchMatchmakingResults({ id: data?.id, statusFilter: "ONGOING" })
      );
    }
    if (data?.id && type !== "banking") {
      dispatch(
        fetchListingTermsConfiguration({
          id: data?.tenderNumber
            ? data?.capacityId || data?.generatorCapacityId
            : data?.id,
          userId: data?.userId,
          type: `${
            data?.consumerId
              ? `consumerId=${data?.consumerId}`
              : `generatorId=${data?.generatorId}`
          }`,
        })
      );
    }
  }, [data, dispatch]);

  const [connection, setConnection] = useState({});
  const [modals, setModals] = React.useState({
    terms: false,
    charges: false,
    consent: false,
    docs: false,
    schedule: false,
  });
  const toggleModal = (modalName, value) =>
    setModals((prev) => ({ ...prev, [modalName]: value }));

  /* ── Computed stats ── */
  const allConnections =
    bilateral?.flatMap((ele) => ele?.bilateralConnections || []) || [];
  const totalQuantity = allConnections.reduce(
    (sum, c) => sum + (Number(c?.quantity) || 0), 0
  );
  const fixedRates = allConnections
    .filter((c) => c?.energyRateType === "FIXED")
    .map((c) => Number(c?.amount));
  const avgRate = fixedRates.length
    ? (fixedRates.reduce((a, b) => a + b, 0) / fixedRates.length).toFixed(1)
    : null;
  const activeSellers = bilateral?.length || 0;
  const totalRequisitions = allConnections.length;

  /* ── Helpers ── */
  const getSellerName = (ele) =>
    data?.consumerId
      ? ele?.capacityGenerator?.generatorName
      : ele?.capacityConsumer?.consumerName;

  const getCapacityName = (ele) =>
    data?.consumerId
      ? ele?.capacityGenerator?.name
      : ele?.capacityConsumer?.name;

  const getAvatar = (name) => (name ? name.slice(0, 2).toUpperCase() : "??");

  const formatRate = (element) => {
    if (element?.energyRateType === "FIXED") return element?.amount;
    if (element?.energyRateType === "DAM_PLUS") return `DAM+${element?.amount}`;
    return `DAM-${element?.amount}`;
  };

  const formatTime = (slot) => {
    if (!slot) return "-";
    const [h, m] = slot.split(":");
    return `${h}:${m}`;
  };

  const formatEndTime = (slot) =>
    slot === "23:59:00" ? "24:00" : formatTime(slot);

  const pageTitle =
    type === "banking"
      ? "Banking detail"
      : data?.tenderNumber
      ? "Tender detail"
      : "Listing detail";

  const startDT = formatISODate(data?.tenderStartDateTime);
  const endDT   = formatISODate(data?.tenderCloseDateTime);

  return (
    <>
      {modals.terms && (
        <ViewFinalizeTerms
          open={modals.terms}
          handleClose={() => toggleModal("terms", false)}
          data={connection}
        />
      )}
      {modals.charges && (
        <Charges
          open={modals.charges}
          onClose={() => toggleModal("charges", false)}
          contractId={connection?.id}
          userType={data?.consumerId ? "consumer" : "generator"}
        />
      )}
      {modals.consent && (
        <ViewConsent
          connectionId={connection.id}
          open={modals.consent}
          onClose={() => toggleModal("consent", false)}
          isAdmin={true}
        />
      )}
      {modals.docs && (
        <ViewDoc
          connectionId={connection.id}
          open={modals.docs}
          handleClose={() => toggleModal("docs", false)}
        />
      )}
      {modals.schedule && (
        <ScheduleModal
          connectionId={connection.id}
          open={modals.schedule}
          handleClose={() => toggleModal("schedule", false)}
        />
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={Styles.adminListingView}>

          {/* ── Navbar ── */}
          <div className={Styles.navbar}>
            <div className={Styles.navLeft}>
              <button className={Styles.backBtn} onClick={handleClose}>
                <ArrowBackIcon style={{ fontSize: 14 }} />
                Back
              </button>
              <div className={Styles.navSep} />
              <span className={Styles.navTitle}>{pageTitle}</span>
            </div>
            <div className={Styles.statusPill}>
              <div className={Styles.statusDot} />
              {data?.status || data?.tenderStatus || "-"}
            </div>
          </div>

          {/* ── Scrollable body ── */}
          <div className={`scroll_bar_y ${Styles.listingBody}`}>

            {type !== "banking" && (
              <>
                {/* Meta card */}
                <div className={Styles.metaCard}>
                  {data?.tenderNumber ? (
                    <>
                      <div className={Styles.metaItem}>
                        <div className={Styles.metaLabel}>Tender Number</div>
                        <div className={Styles.metaValue}>{data?.tenderNumber}</div>
                        {data?.auctionNumber && (
                          <div className={Styles.metaSub}>Auction: {data?.auctionNumber}</div>
                        )}
                      </div>
                      <div className={Styles.metaItem}>
                        <div className={Styles.metaLabel}>Start Date &amp; Time</div>
                        <div className={Styles.metaValue}>{startDT.date}</div>
                        {startDT.time && <div className={Styles.metaSub}>{startDT.time}</div>}
                      </div>
                      <div className={Styles.metaItem}>
                        <div className={Styles.metaLabel}>End Date &amp; Time</div>
                        <div className={Styles.metaValue}>{endDT.date}</div>
                        {endDT.time && <div className={Styles.metaSub}>{endDT.time}</div>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={Styles.metaItem}>
                        <div className={Styles.metaLabel}>Listing Number</div>
                        <div className={Styles.metaValue}>{data?.name}</div>
                        <div className={Styles.metaSub}>
                          {data?.energyFuelType} · {data?.voltageLevel}
                        </div>
                      </div>
                      <div className={Styles.metaItem}>
                        <div className={Styles.metaLabel}>Listing Exp. Date &amp; Time</div>
                        <div className={Styles.metaValue}>{formatDateType(data?.listingExpDate)}</div>
                        <div className={Styles.metaSub}>{data?.listingExpTime}</div>
                      </div>
                      <div className={Styles.metaItem}>
                        <div className={Styles.metaLabel}>Terms Exp. Date &amp; Time</div>
                        <div className={Styles.metaValue}>{formatDateType(data?.termsExpDate)}</div>
                        <div className={Styles.metaSub}>{data?.termsExpTime}</div>
                      </div>
                    </>
                  )}
                </div>

                {/* Stats row */}
                <div className={Styles.statsRow}>
                  <div className={`${Styles.stat} ${Styles.sGreen}`}>
                    <div className={Styles.statLabel}>Total quantity</div>
                    <div className={Styles.statVal}>
                      {totalQuantity} <span className={Styles.statUnit}>MW</span>
                    </div>
                  </div>
                  <div className={`${Styles.stat} ${Styles.sOrange}`}>
                    <div className={Styles.statLabel}>Avg. energy rate</div>
                    <div className={Styles.statVal}>
                      {avgRate ? (
                        <>₹{avgRate} <span className={Styles.statUnit}>/kWh</span></>
                      ) : "-"}
                    </div>
                  </div>
                  <div className={`${Styles.stat} ${Styles.sTeal}`}>
                    <div className={Styles.statLabel}>Active sellers</div>
                    <div className={Styles.statVal}>{activeSellers}</div>
                  </div>
                  <div className={`${Styles.stat} ${Styles.sAmber}`}>
                    <div className={Styles.statLabel}>Total requisitions</div>
                    <div className={Styles.statVal}>{totalRequisitions}</div>
                  </div>
                </div>

                {/* Section header */}
                <div className={Styles.secHd}>
                  <span className={Styles.secHdTitle}>Self requisitions</span>
                  <span className={Styles.secCount}>
                    {activeSellers} {activeSellers === 1 ? "seller" : "sellers"}
                  </span>
                </div>
              </>
            )}

            {/* Sellers / banking */}
            <div className={Styles.sellers}>
              {type === "banking" ? (
                bankingList?.matches?.map((ele) => (
                  <DiscomDetails key={ele?.id} ele={ele} />
                ))
              ) : (
                bilateral?.map((ele, index) => {
                  const sellerName   = getSellerName(ele);
                  const capacityName = getCapacityName(ele);
                  const connCount    = ele?.bilateralConnections?.length || 0;
                  return (
                    <div key={index} className={Styles.sellerCard}>

                      {/* Seller header */}
                      <div className={Styles.sellerHd}>
                        <div className={Styles.sellerLeft}>
                          <div className={Styles.avatar}>{getAvatar(sellerName)}</div>
                          <div>
                            <div className={Styles.sellerName}>{sellerName}</div>
                            <div className={Styles.sellerId}>{capacityName}</div>
                          </div>
                        </div>
                        <div className={Styles.connBadge}>
                          {connCount} {connCount === 1 ? "requisition" : "requisitions"}
                        </div>
                      </div>

                      {/* Column headers */}
                      <div className={Styles.colHds}>
                        <div className={Styles.colHd}>Quantity</div>
                        <div className={Styles.colHd}>Energy Rate</div>
                        <div className={Styles.colHd}>Date Range</div>
                        <div className={Styles.colHd}>Time Range</div>
                      </div>

                      {/* Connection rows */}
                      {ele?.bilateralConnections?.map((element, idx) => (
                        <div key={idx} className={Styles.connRow}>
                          <div>
                            <span className={`${Styles.badge} ${Styles.bQty}`}>
                              <BoltIcon style={{ fontSize: 12 }} />
                              {element?.quantity} MW
                            </span>
                          </div>
                          <div>
                            <span className={`${Styles.badge} ${Styles.bRate}`}>
                              <CurrencyRupeeIcon style={{ fontSize: 12 }} />
                              {formatRate(element)} / kWh
                            </span>
                          </div>
                          <div className={Styles.dateRange}>
                            <span className={Styles.dChip}>{formatDateType(element?.dateStartAt)}</span>
                            <span className={Styles.dArrow}>
                              <ArrowForwardIcon style={{ fontSize: 11 }} />
                            </span>
                            <span className={Styles.dChip}>{formatDateType(element?.dateEndAt)}</span>
                          </div>
                          <div className={Styles.timeVal}>
                            <AccessTimeIcon style={{ fontSize: 13, color: "#b0aebb" }} />
                            {formatTime(element?.slotStartAt)} – {formatEndTime(element?.slotEndAt)}
                          </div>
                        </div>
                      ))}

                      {/* Actions bar */}
                      <div className={Styles.actionsBar}>
                        <button
                          className={`${Styles.act} ${Styles.actPrimary}`}
                          onClick={() => { toggleModal("terms", true); setConnection(ele); }}
                        >View Terms</button>
                        <button
                          className={`${Styles.act} ${Styles.actPrimary}`}
                          onClick={() => { toggleModal("charges", true); setConnection(ele); }}
                        >View Charges</button>
                        <button
                          className={`${Styles.act} ${Styles.actPrimary}`}
                          onClick={() => { toggleModal("docs", true); setConnection(ele); }}
                        >View Docs</button>
                        <button
                          className={`${Styles.act} ${Styles.actPrimary}`}
                          onClick={() => { toggleModal("consent", true); setConnection(ele); }}
                        >View Consent</button>
                        <button
                          className={`${Styles.act} ${Styles.actPrimary}`}
                          onClick={() => { toggleModal("schedule", true); setConnection(ele); dispatch(fetchSchedule(ele?.id)); }}
                        >View Schedule</button>
                        <div className={Styles.actSep} />
                        <button
                          className={`${Styles.act} ${Styles.actChat}`}
                          onClick={() =>
                            openBilateralChat(dispatch, {
                              header:   data?.generatorId ? ele?.capacityGenerator?.name : ele?.capacityConsumer?.name,
                              fromUser: data?.consumerId  ? ele?.capacityGenerator?.name : ele?.capacityConsumer?.name,
                              toUser:   data?.generatorId ? ele?.capacityGenerator?.name : ele?.capacityConsumer?.name,
                              hidechat: true,
                            })
                          }
                        >View Chat</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ListingDetails;
